<template>
    <section class="flex flex-col gap-4" data-test="rnd-schedules">
        <div class="flex flex-col gap-1">
            <h2 class="m-0 text-xl font-extrabold">Monthly schedules</h2>
            <p class="m-0 text-sm text-slate-500">
                Teams with monthly auto-run on that are not suspended or closed. Each starts a run at 08:00 US Eastern
                on the 17th, which applies its resets without waiting for approval. A team with a run already active at
                that moment is skipped for the month, and a missed start is not made up later: start that team's run
                with Run check now. A team switched on after this month's 17th first runs next month.
            </p>
        </div>

        <vx-alert v-if="attentionCount > 0" color="danger" :closeable="false" data-test="schedules-attention">
            {{ attentionText }}
        </vx-alert>

        <vx-data-table :columns="columns" :rows="schedules" rowKey="team_id" :loading="loading">
            <template v-slot:cell-team_id="{ row }">
                <kiosk-team-cell :teamId="row.team_id" :teamName="row.team_name" :teamStatus="row.team_status" />
            </template>

            <template v-slot:cell-company_id="{ row }">
                <span class="font-mono">{{ row.company_id }}</span>
            </template>

            <template v-slot:cell-next_run_at="{ row }">
                <span data-test="next-run">{{ shortDateTime(row.next_run_at) }}</span>
            </template>

            <template v-slot:cell-latest_month="{ row }">
                <div v-if="row.latest_month !== null" class="flex min-w-0 items-center gap-2">
                    <span class="shrink-0 text-xs text-slate-500" data-test="latest-month-date">
                        {{ shortDate(row.latest_month.starts_at) }}
                    </span>
                    <template v-if="row.latest_month.state === 'started' && row.latest_month.run !== null">
                        <span class="shrink-0 text-slate-500" data-test="latest-month-run">
                            #{{ row.latest_month.run.id }}
                        </span>
                        <kiosk-rnd-run-status :run="row.latest_month.run" />
                    </template>
                    <template v-else-if="row.latest_month.state === 'skipped'">
                        <vx-badge color="amber" size="xs" data-test="latest-month-state">Skipped</vx-badge>
                        <span class="min-w-0 truncate text-xs text-slate-500" data-test="latest-month-reason">
                            {{ skippedReason(row.latest_month) }}
                        </span>
                    </template>
                    <vx-badge
                        v-else-if="row.latest_month.state === 'missed'"
                        color="rose"
                        size="xs"
                        data-test="latest-month-state"
                    >
                        Missed
                    </vx-badge>
                </div>
                <span v-else class="text-slate-500" data-test="latest-month-none">Not due yet</span>
            </template>

            <template v-slot:cell-last_run="{ row }">
                <div v-if="row.last_run !== null" class="flex min-w-0 items-center gap-2">
                    <kiosk-rnd-run-status :run="row.last_run" />
                    <span class="shrink-0 text-xs text-slate-500" data-test="last-run-date">
                        {{ runDateLabel(row.last_run.finished_at ?? row.last_run.snapshot_at) }}
                    </span>
                </div>
                <span v-else class="text-slate-500" data-test="no-last-run">Never run</span>
            </template>

            <template v-slot:empty>
                <div class="flex flex-col items-center gap-6 text-xl text-slate-500">
                    No team has monthly auto-run on
                </div>
            </template>
        </vx-data-table>
    </section>
</template>

<script lang="ts" setup>
import { VxAlert, VxBadge, VxDataTable, type VxDataTableColumn } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import KioskTeamCell from '~/components/kiosk/KioskTeamCell.vue';
import { runDateLabel } from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import KioskRndRunStatus from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunStatus.vue';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRunStatus, RndSchedule, RndScheduleMonth } from '~/services/adminRndClient.types';
import { shortDate, shortDateTime } from '~/utils/date';
import dayjs from '~/utils/dayjs';

const props = defineProps<{ refreshKey: number }>();

const toasted = useToasted();

const schedulesCancelKey = Symbol('kiosk-rnd-schedules');

const columns: VxDataTableColumn<RndSchedule>[] = [
    { key: 'team_id', label: 'Team', width: 240 },
    { key: 'company_id', label: 'RND Company ID', width: 140 },
    { key: 'next_run_at', label: 'Next run', width: 230 },
    { key: 'latest_month', label: 'Latest Monthly start', width: 320 },
    { key: 'last_run', label: 'Last run', width: 360 },
];

const ATTENTION_STATUSES: ReadonlySet<RndRunStatus> = new Set<RndRunStatus>([
    'cancelled',
    'completed_with_errors',
    'expired',
    'failed',
    'partially_applied',
    'stopped',
]);

const schedules = ref<RndSchedule[]>([]);
const loading = ref(true);

const skippedReason = (month: RndScheduleMonth): string =>
    month.run === null ? 'A run was active' : `Run #${month.run.id} was active`;

const unansweredStart = (schedule: RndSchedule): boolean => {
    const month = schedule.latest_month;

    if (month === null || month.state === 'started') {
        return false;
    }

    return schedule.last_run === null || dayjs.utc(schedule.last_run.snapshot_at).isBefore(dayjs.utc(month.starts_at));
};

const needsAttention = (schedule: RndSchedule): boolean =>
    unansweredStart(schedule) || (schedule.last_run !== null && ATTENTION_STATUSES.has(schedule.last_run.status));

const attentionCount = computed(() => schedules.value.filter(needsAttention).length);
const attentionText = computed(() =>
    attentionCount.value === 1
        ? '1 team needs attention: its latest Monthly run was skipped or missed and no run has started since, or its last run did not complete.'
        : `${attentionCount.value} teams need attention: their latest Monthly run was skipped or missed and no run has started since, or their last run did not complete.`
);

const loadSchedules = async () => {
    loading.value = true;

    try {
        const { data } = await adminRndClient.getSchedules({ cancelKey: schedulesCancelKey });

        schedules.value = data.data;
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        schedules.value = [];
        loading.value = false;
        toasted.global.platform_error();
    }
};

watch(
    () => props.refreshKey,
    () => {
        loadSchedules();
    }
);

onMounted(() => {
    loadSchedules();
});
</script>
